import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Building2, Eye, CreditCard, Crown, FileText, ChevronRight } from '@/components/icons';
import { TopBarBackButton } from '@/components/topbar/TopBarBackButton';
import { AppLayout } from '@/components/AppLayout';
import { Card } from '@/components/ui/card';
import { DesktopPage } from '@/components/desktop/DesktopPage';
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader';
import { useIsDesktop } from '@/components/ui/use-mobile';

export const Route = createFileRoute('/_auth/operations/settings/')({
  component: AdminSettingsRoute,
});

interface SettingsMenuItemProps {
  icon: React.ReactNode;
  title: string;
  onClick?: () => void;
}

function SettingsMenuItem({ icon, title, onClick }: SettingsMenuItemProps) {
  return (
    <button
      onClick={onClick}
      className="flex gap-3 items-center h-12 px-3 py-[7.5px] rounded-md w-full text-left"
    >
      <div className="size-5 text-content-primary-bold">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-content-primary-bold tracking-[0.07px] leading-6">{title}</p>
      </div>
      <ChevronRight className="size-5 text-content-primary-bold" />
    </button>
  );
}

function AdminSettingsRoute() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const body = (
    <>
      {/* Menu Card */}
      <Card className="flex flex-col gap-2">
        <SettingsMenuItem
          icon={<Building2 className="size-5" />}
          title="Company Info"
          onClick={() => navigate({ to: '/operations/settings/companyinfo' })}
        />
        <div className="h-px bg-border" />
        <SettingsMenuItem
          icon={<Crown className="size-5" />}
          title="Owners & Admins"
          onClick={() => navigate({ to: '/operations/settings/owneradmins' })}
        />
        <div className="h-px bg-border" />
        <SettingsMenuItem
          icon={<CreditCard className="size-5" />}
          title="Subscription Status"
          onClick={() => navigate({ to: '/operations/subscriptions' })}
        />
        <div className="h-px bg-border" />
        <SettingsMenuItem
          icon={<Eye className="size-5" />}
          title="Privacy Policy"
          onClick={() => navigate({ to: '/operations/settings/privacy' })}
        />
        <div className="h-px bg-border" />
        <SettingsMenuItem
          icon={<FileText className="size-5" />}
          title="Terms of Service"
          onClick={() => navigate({ to: '/operations/settings/terms' })}
        />
      </Card>
    </>
  );

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Admin Settings"
          breadcrumb={
            <span>
              <Link to="/operations" className="hover:underline">
                Operations
              </Link>
              <span className="text-content-primary-subtle"> / Settings</span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    );
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title="Admin Settings" />}
      className="pt-6"
    >
      {body}
    </AppLayout>
  );
}
